import { FormEvent, useState } from "react";
import { LockKeyhole, Loader2 } from "lucide-react";

import { BrandMark } from "@/components/brand-mark";


export function LoginForm() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ login, password, remember }),
      });
      if (!response.ok) {
        setError("Login or password is incorrect");
        return;
      }
      window.location.assign("/");
    } catch {
      setError("Login is temporarily unavailable");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--hc-bg)] px-4 py-10 text-[var(--hc-text)]">
      <section className="w-full max-w-md rounded-xl border border-[var(--hc-border)] bg-[var(--hc-surface-1)] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.35)] sm:p-8">
        <BrandMark priority />
        <h1 className="sr-only">Sign in to Horosho Capital</h1>

        <form className="mt-8 space-y-4" onSubmit={submit} aria-busy={busy}>
          <label className="block" htmlFor="login">
            <span className="text-sm font-medium text-[var(--hc-muted)]">Login</span>
            <input data-login-input id="login" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="username" className="mt-2 min-h-11 w-full rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-bg)] px-3 py-2.5 text-base text-[var(--hc-text)] placeholder:text-[var(--hc-subtle)]" placeholder="Enter login" />
          </label>

          <label className="block" htmlFor="password">
            <span className="text-sm font-medium text-[var(--hc-muted)]">Password</span>
            <div className="mt-2 flex min-h-11 items-center rounded-md border border-[var(--hc-border-strong)] bg-[var(--hc-bg)] px-3 focus-within:outline focus-within:outline-3 focus-within:outline-offset-2 focus-within:outline-[var(--hc-blue)]">
              <LockKeyhole size={17} className="text-[var(--hc-subtle)]" aria-hidden="true" />
              <input data-login-input id="password" value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" className="min-w-0 flex-1 bg-transparent px-2 py-2.5 text-base text-[var(--hc-text)] outline-none placeholder:text-[var(--hc-subtle)]" placeholder="Enter password" />
            </div>
          </label>

          <label className="flex min-h-11 items-center justify-between gap-3 rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-3 py-2.5 text-sm text-[var(--hc-muted)]">
            <span>Remember me on this device</span>
            <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} className="size-5 accent-[var(--hc-blue)]" />
          </label>

          {error ? <p role="alert" className="rounded-md border border-[var(--hc-negative)]/40 bg-[var(--hc-negative-soft)] px-3 py-2 text-sm text-[var(--hc-negative)]">{error}</p> : null}

          <button type="submit" disabled={busy || !login || !password} aria-disabled={busy || !login || !password} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-[var(--hc-crimson)] bg-[var(--hc-crimson)] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#d00038] disabled:cursor-not-allowed disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : null}
            {busy ? "Opening…" : "Open terminal"}
          </button>
        </form>
      </section>
    </main>
  );
}
